// Loads the projects from the stay platform API and reshapes them into the
// exact objects the retired src/Projects/*.js modules hardcoded, so every
// component keeps working unchanged.
//
// Requests are same-origin: the dev server proxies /api and /uploads to the
// backend (package.json "proxy"), production nginx does the same. For pointing
// a running site at another backend: ?api_base=https://... (sticky for the
// session; an empty ?api_base= clears it).
//
// Out of scope on purpose: sales. `forSale` and the buy-now flow stay
// hardcoded to MOH26 and the booking form keeps posting to the old endpoint
// until the leads pipeline is wired up.

const BASE_KEY = 'drfah_api_base';


function apiBase() {
  const qs = new URLSearchParams(window.location.search);
  if (qs.has('api_base')) {
    const base = qs.get('api_base');
    if (base) sessionStorage.setItem(BASE_KEY, base);
    else sessionStorage.removeItem(BASE_KEY);
  }
  return sessionStorage.getItem(BASE_KEY) || '';
}

function absolute(url) {
  if (!url) return url;
  return url.startsWith('/') ? apiBase() + url : url;
}

async function getJSON(path, lang) {
  const res = await fetch(apiBase() + path, {
    headers: lang ? { 'Accept-Language': lang } : {},
  });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return res.json();
}

const cf = (site, key) => (site.custom_fields || []).find((f) => f.key === key)?.value;

// The imported extra-field values are ready-made display strings ("Up to 190
// m²"), so a stat renders whole as `value` with no separate unit/prefix.
function buildStats(site) {
  const stats = {};
  for (const key of ['units', 'floors_in_unit', 'built_up_area_per_unit', 'plot_area_per_unit', 'total_built_up_area']) {
    const v = cf(site, key);
    if (v) stats[key] = { value: v, unit: '' };
  }
  return stats;
}

function mapPlanToFloorPlan(plan, legendField, nameField) {
  const drawing = (plan.drawings || [])[0];
  const legends = (plan.annotations || [])
    .filter((a) => a.kind === 'legend_marker')
    .map((a) => `${a.marker_index ?? ''} ${a[legendField] || a.label_en || ''}`.trim());
  return {
    name: plan[nameField] || plan.name_en || '',
    img: absolute(drawing?.path || drawing?.url),
    area: plan.area ? `${plan.area} ${plan.area_unit === 'sqm' || !plan.area_unit ? 'm²' : plan.area_unit}` : '',
    descriptionList: legends,
  };
}

// buildProject reshapes one property (one language) into the static shape.
// saleProperties / saleSerials: which property serial ids and unit-instance
// serial ids hold a LIVE sale offer — what "for sale" MEANS now. The hardcoded
// moh26 flag this replaces kept selling a project after its offers closed and
// could never sell a second one.
function buildProject({ site, lang, floorUnits, propPlans, instancePlans, saleProperties, saleSerials }) {
  const ar = lang === 'ar';
  const slug = site.slug;
  const nameField = ar ? 'name_ar' : 'name_en';
  const legendField = ar ? 'label_ar' : 'label_en';

  const gallery = (site.gallery || []).map((g) => ({
    url: absolute(g.url), mime: g.mime_type || '',
    thumb: absolute(g.thumbnail_url || g.url),
    description: (ar ? g.caption_ar : g.caption) || '',
  }));
  const photos = gallery.filter((g) => g.mime !== 'image/gif');
  const gifs = gallery.filter((g) => g.mime === 'image/gif').map((g) => g.url);
  // A hero row is a byte-copy of its gallery photo with its own URL. The
  // header carousel locates the hero INSIDE the gallery by identity
  // (findIndex), so map each hero back to its gallery twin via the original
  // filename the payload carries.
  const galleryUrlByName = Object.fromEntries((site.gallery || []).map((g) => [g.name, absolute(g.url)]));
  const heroes = (site.hero_images || []).map((h) => ({
    url: galleryUrlByName[h.name] || absolute(h.url),
    mime: h.mime_type || '',
  }));

  // Floor plans: unit-type plans where the project publishes products,
  // property-level plans otherwise (Nakheel's floor plates, AlUyaynah's main
  // building).
  const typePlans = (floorUnits || []).flatMap((u) => (u.plans || []).map((p) => mapPlanToFloorPlan(p, legendField, nameField)));
  const propertyFloorPlans = (propPlans || [])
    .filter((p) => p.kind === 'floor_plan')
    .map((p) => mapPlanToFloorPlan(p, legendField, nameField));
  const floorPlans = typePlans.length ? typePlans : propertyFloorPlans;

  // The site plan overlay: cover + one hotspot per region annotation, its
  // highlight image resolved among the same plan's drawings.
  const sitePlan = (propPlans || []).find((p) => p.kind === 'site_plan');
  let plans = {};
  if (sitePlan) {
    const drawingByID = Object.fromEntries((sitePlan.drawings || []).map((d) => [d.id, absolute(d.path || d.url)]));
    // The platform generates small derivatives for plan drawings (media_variants
    // kind='thumbnail'); a preview box wants those, the overlay keeps the full
    // drawing. Absent (variant not generated yet) falls back to the full file.
    const thumbByID = Object.fromEntries((sitePlan.drawings || []).map((d) => [d.id, absolute(d.thumbnail || d.path || d.url)]));
    const cover = absolute((sitePlan.drawings || [])[0]?.path || (sitePlan.drawings || [])[0]?.url);
    const coverThumb = absolute((sitePlan.drawings || [])[0]?.thumbnail) || cover;
    const regions = (sitePlan.annotations || []).filter((a) => a.kind === 'region_link');
    plans = {
      cover,
      coverThumb,
      plans: regions.map((a) => {
        const geom = a.geometry || {};
        return {
          id: String(a.target_id || a.media_id),
          name: (ar ? a.label_ar : a.label_en) || a.label_en || '',
          left: `${geom.left}%`, top: `${geom.top}%`,
          width: `${geom.width}%`, height: `${geom.height}%`,
          image: drawingByID[a.highlight_media_id] || cover,
          thumb: thumbByID[a.highlight_media_id] || coverThumb,
          price: 0,
          size: a.target_space || 0,
          availabile: saleSerials.has(String(a.target_id)),
          floorPlans: (instancePlans[a.target_id] || []).map((p) => mapPlanToFloorPlan(p, legendField, nameField)),
        };
      }),
    };
  }

  const loc = site.location || {};
  // Presentation knobs live as extra fields on the property, not in this
  // bundle: which text section the diagrams illustrate, and the original
  // ?project_id= casing old links and the SEO map key on.
  const sectionIdx = Number(cf(site, 'diagram_section') ?? 1);

  return {
    // The property slug IS the routing id; consumers match it
    // case-insensitively so links with the old casing keep working.
    id: slug,
    // The platform's serial id, so offer payloads (property_id) join to this
    // project by IDENTITY rather than display-name equality, which broke for
    // Arabic names and whitespace edits.
    property_id: site.id,
    name: (ar ? site.name_ar : site.name) || site.name,
    residential_Unit_Type: (ar ? site.kind_label_ar : site.kind_label_en) || '',
    scope: cf(site, 'scope') || '',
    status: cf(site, 'project_status') || '',
    expected_completion: cf(site, 'expected_completion'),
    starting_price: null,
    starting_price_text: cf(site, 'starting_price') || null,
    // The largest bedroom count among the project's published unit types —
    // what the search's bedrooms range filters on.
    number_of_bedrooms: (() => {
      const beds = (floorUnits || []).map((u) => u.numberOfBedRooms).filter((n) => n != null);
      return beds.length ? Math.max(...beds) : null;
    })(),
    clickable: true,
    location: (ar ? loc.district_ar : loc.district) || null,
    book_a_tour_text: cf(site, 'book_a_tour_text'),
    area: cf(site, 'area') || '',
    stats: buildStats(site),
    nearby_description: (ar ? loc.address_ar : loc.address) || '',
    main_images: heroes.filter((h) => !h.mime.startsWith('video/')).map((h) => h.url),
    opener_video: heroes.find((h) => h.mime.startsWith('video/'))?.url,
    text_sections: ((ar ? site.description_ar : site.description) || '').split('\n\n').filter(Boolean),
    section_images: gifs.length ? { [sectionIdx]: gifs } : {},
    // No site plan? The detail page still shows a plans section — use the
    // first floor plan as its cover rather than an image that never loads.
    plans_cover: plans.cover || floorPlans[0]?.img,
    plnas_description: cf(site, 'plans_description') || '',
    seo: {
      title: cf(site, 'seo_title'),
      description: cf(site, 'seo_description'),
      keywords: cf(site, 'seo_keywords'),
    },
    location_dev: loc.coordinates
      ? { latitude: loc.coordinates.lat, longitude: loc.coordinates.lng, google_maps_url: cf(site, 'google_maps_url') }
      : {},
    images: photos.map((g) => ({ image: g.url, thumbnail: g.thumb, description: g.description })),
    hasPlansOverlay: !!(plans.plans && plans.plans.length),
    hasPlans: floorPlans.length > 0 || !!(plans.plans && plans.plans.length),
    hasLocation: !!loc.coordinates,
    forSale: saleProperties.has(site.id),
    plans,
    floorPlans,
  };
}

export async function loadProjectsFromApi() {
  const [sitesEn, sitesAr, floorPlansAll, saleListings] = await Promise.all([
    getJSON('/api/drfah/websites/', 'en'),
    getJSON('/api/drfah/websites/', 'ar'),
    getJSON('/api/drfah/floor-plans/'),
    getJSON('/api/drfah/listings?mode=sale'),
  ]);
  // Only offers OPEN for registration count: a waiting_list offer is held, and
  // the buy-now flow must neither show its villa nor render a buy-now button
  // for a project whose homes are all held.
  const openSale = (saleListings.listings || []).filter((l) => !l.waiting_list);
  const saleProperties = new Set(openSale.map((l) => l.property_id));
  const saleSerials = new Set(openSale.map((l) => String(l.subject?.serial_id)));

  const arBySlug = Object.fromEntries(sitesAr.map((s) => [s.slug, s]));
  const unitsByProperty = {};
  for (const u of floorPlansAll.units || []) {
    (unitsByProperty[u.property] ||= []).push(u);
  }

  const ordered = [...sitesEn].sort(
    (a, b) => Number(cf(a, 'display_order') ?? 99) - Number(cf(b, 'display_order') ?? 99)
  );

  // All per-property requests run CONCURRENTLY — done sequentially this was
  // 11+ round-trips in a row and dominated the time a visitor stared at the
  // static prerender before the app took over.
  const built = await Promise.all(ordered.map(async (site) => {
    const propPlans = (await getJSON(`/api/drfah/plans?subject_type=property&subject_id=${site.id}`)).plans || [];
    const sitePlan = propPlans.find((p) => p.kind === 'site_plan');
    const targets = [...new Set((sitePlan?.annotations || [])
      .filter((a) => a.kind === 'region_link' && a.target_type === 'unit_instance')
      .map((a) => a.target_id))];
    const instancePlans = {};
    await Promise.all(targets.map(async (t) => {
      instancePlans[t] = (await getJSON(`/api/drfah/plans?subject_type=unit_instance&subject_id=${t}`)).plans || [];
    }));
    const common = { floorUnits: unitsByProperty[site.name], propPlans, instancePlans, saleProperties, saleSerials };
    return {
      en: buildProject({ site, lang: 'en', ...common }),
      ar: buildProject({ site: arBySlug[site.slug] || site, lang: 'ar', ...common }),
    };
  }));
  return { en: built.map((b) => b.en), ar: built.map((b) => b.ar) };
}
